import {FormControl, InputLabel, MenuItem, Paper, Select, Stack} from "@mui/material";
import React, {useState} from "react";
import {GlobalState} from "../../global/GlobalState";
import {ColorThemePreference, UserPreferences} from "../../data/preferences/UserPreferences";
import {translateFunction} from "../../localization/TranslationService";
import {Instant, ZoneId} from "@js-joda/core";
import {Localization} from "../../localization/Localization";
import {LangDutch} from "../../localization/language/LangDutch";

const translate = translateFunction();

export function UserSettingsPage() {
	const userPreferences = GlobalState.userPreferences;
	
	function setPreference(userPreference: UserPreferences) {
		GlobalState.userPreferences = userPreference;
	}
	
	const localization = new Localization(LangDutch);
	const [exampleInstant] = useState(Instant.now());
	const exampleDate = exampleInstant.atZone(ZoneId.systemDefault()).toLocalDate();
	
	const datePatternOptions = [
		"yyyy/MM/dd",
		"dd/MM/yyyy",
	];
	
	const dateTimePatternShortOptions = [
		"yyyy/MM/dd HH:mm",
		"dd/MM/yyyy HH:mm",
		"yyyy/MM/dd HH:mm '('xxx')'",
		"dd/MM/yyyy HH:mm '('xxx')'",
	];
	
	const dateTimePatternOptions = [
		"yyyy/MM/dd HH:mm:ss",
		"dd/MM/yyyy HH:mm:ss",
		"yyyy/MM/dd HH:mm:ss '('xxx')'",
		"dd/MM/yyyy HH:mm:ss '('xxx')'",
	];
	
	const dateTimePatternLongOptions = [
		"yyyy/MM/dd HH:mm:ss.SSS",
		"dd/MM/yyyy HH:mm:ss.SSS",
		"yyyy/MM/dd HH:mm:ss.SSS '('xxx')'",
		"dd/MM/yyyy HH:mm:ss.SSS '('xxx')'",
	];
	
	const inputDateTimePatternOptions = [
		"dd/MM/yyyy HH:mm:ss",
		"yyyy/MM/dd HH:mm:ss",
	];
	
	return <Paper style={{
		height: "100%",
	}}>
		<h1>{translate("Settings")}</h1>
		<Stack spacing={3}>
			<FormControl key="user-settings-language">
				<InputLabel
					id="user-settings-language-select-label"
				>{translate("Language")}</InputLabel>
				<Select
					labelId="user-settings-language-select-label"
					label={translate("Language")}
					value={userPreferences.languageKeyRequired}
					onChange={event => setPreference(userPreferences.copy({ languageKey: event.target.value }))}
				>
					{GlobalState.supportedLanguages.map(it => {
						return <MenuItem
							key={it.key}
							value={it.key}>
							{translate(`language:${it.key}`)}
						</MenuItem>;
					})}
				</Select>
			</FormControl>
			
			<FormControl key="user-settings-theme">
				<InputLabel
					id="user-settings-theme-select-label"
				>{translate("Color Theme")}</InputLabel>
				<Select
					labelId="user-settings-theme-select-label"
					label={translate("Color Theme")}
					value={userPreferences.colorTheme}
					onChange={event => setPreference(userPreferences.copy({ colorTheme: event.target.value as ColorThemePreference }))}
				>
					<MenuItem key="none" value={"none"}>{translate("No preference")}</MenuItem>
					<MenuItem key="light" value={"light"}>{translate("Light")}</MenuItem>
					<MenuItem key="dark" value={"dark"}>{translate("Dark")}</MenuItem>
					<MenuItem key="system-default" value={"system-default"}>{translate("System Default")}</MenuItem>
				</Select>
			</FormControl>
			
			<FormControl key="user-settings-datePattern">
				<InputLabel
					id="user-settings-datePattern-select-label"
				>{translate("Date Pattern")}</InputLabel>
				<Select
					labelId="user-settings-datePattern-select-label"
					label={translate("Date Pattern")}
					value={userPreferences.datePattern ?? "none"}
					onChange={event => setPreference(userPreferences.copy({ datePattern: event.target.value === "none" ? null : event.target.value }))}
				>
					<MenuItem key="none" value={"none"}>{translate("No preference")}</MenuItem>
					{datePatternOptions.map(it =>
						<MenuItem key={it} value={it}>{localization.formatDate(exampleDate, it)}</MenuItem>)}
				</Select>
			</FormControl>
			
			<FormControl key="user-settings-dateTimePatternShort">
				<InputLabel
					id="user-settings-dateTimePatternShort-select-label"
				>{translate("Date/Time Pattern (Short)")}</InputLabel>
				<Select
					labelId="user-settings-dateTimePatternShort-select-label"
					label={translate("Date/Time Pattern (Short)")}
					value={userPreferences.dateTimePatternShort ?? "none"}
					onChange={event => setPreference(userPreferences.copy({ dateTimePatternShort: event.target.value === "none" ? null : event.target.value }))}
				>
					<MenuItem key="none" value={"none"}>{translate("No preference")}</MenuItem>
					{dateTimePatternShortOptions.map(it =>
						<MenuItem key={it} value={it}>{localization.formatDateTime(exampleInstant, it)}</MenuItem>)}
				</Select>
			</FormControl>
			
			<FormControl key="user-settings-dateTimePattern">
				<InputLabel
					id="user-settings-dateTimePattern-select-label"
				>{translate("Date/Time Pattern")}</InputLabel>
				<Select
					labelId="user-settings-dateTimePattern-select-label"
					label={translate("Date/Time Pattern")}
					value={userPreferences.dateTimePattern ?? "none"}
					onChange={event => setPreference(userPreferences.copy({ dateTimePattern: event.target.value === "none" ? null : event.target.value }))}
				>
					<MenuItem key="none" value={"none"}>{translate("No preference")}</MenuItem>
					{dateTimePatternOptions.map(it =>
						<MenuItem key={it} value={it}>{localization.formatDateTime(exampleInstant, it)}</MenuItem>)}
				</Select>
			</FormControl>
			
			<FormControl key="user-settings-dateTimePatternLong">
				<InputLabel
					id="user-settings-dateTimePatternLong-select-label"
				>{translate("Date/Time Pattern (Long)")}</InputLabel>
				<Select
					labelId="user-settings-dateTimePatternLong-select-label"
					label={translate("Date/Time Pattern (Long)")}
					value={userPreferences.dateTimePatternLong ?? "none"}
					onChange={event => setPreference(userPreferences.copy({ dateTimePatternLong: event.target.value === "none" ? null : event.target.value }))}
				>
					<MenuItem key="none" value={"none"}>{translate("No preference")}</MenuItem>
					{dateTimePatternLongOptions.map(it =>
						<MenuItem key={it} value={it}>{localization.formatDateTime(exampleInstant, it)}</MenuItem>)}
				</Select>
			</FormControl>
			
			<FormControl key="user-settings-dateTimePatternInput">
				<InputLabel
					id="user-settings-dateTimePatternInput-select-label"
				>{translate("Date/Time Pattern (Input)")}</InputLabel>
				<Select
					labelId="user-settings-dateTimePatternInput-select-label"
					label={translate("Date/Time Pattern (Input)")}
					value={userPreferences.inputDateTimePattern ?? "none"}
					onChange={event => setPreference(userPreferences.copy({ inputDateTimePattern: event.target.value === "none" ? null : event.target.value }))}
				>
					<MenuItem key="none" value={"none"}>{translate("No preference")}</MenuItem>
					{inputDateTimePatternOptions.map(it =>
						<MenuItem key={it} value={it}>{localization.formatDateTime(exampleInstant, it)}</MenuItem>)}
				</Select>
			</FormControl>
		</Stack>
	</Paper>;
}
